// ProctoringPanel — floating camera feed + integrity badge for voice interviews.
//
// Shows:
//   - Live camera preview (small, top-right corner)
//   - Integrity score with colour coding
//   - Violation count + latest violation message
//
// Props:
//   videoRef         — ref to the <video> element (hook manages srcObject)
//   integrityScore   — 0-100
//   riskLevel        — "low" | "medium" | "high"
//   violations       — array of { type, description, severity, timestamp }
//   active           — whether the interview is in progress

import React, { useState } from "react";
import { Shield, AlertTriangle, Camera, CameraOff, ChevronDown, ChevronUp } from "lucide-react";

const RISK_STYLE = {
  low:    { bar: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Low risk" },
  medium: { bar: "bg-amber-500",   badge: "bg-amber-50   text-amber-700   border-amber-200",   label: "Med risk" },
  high:   { bar: "bg-rose-500",    badge: "bg-rose-50    text-rose-700    border-rose-200",     label: "High risk" },
};

const SEV_DOT = {
  high:   "bg-rose-500",
  medium: "bg-amber-500",
  low:    "bg-slate-400",
};

export default function ProctoringPanel({ videoRef, integrityScore, riskLevel, violations = [], active }) {
  const [expanded, setExpanded]   = useState(false);
  const [cameraOk, setCameraOk]   = useState(true);

  const style    = RISK_STYLE[riskLevel] || RISK_STYLE.low;
  const vCount   = violations.length;
  const latest   = violations[vCount - 1] || null;
  const score    = Math.max(0, Math.min(100, integrityScore ?? 100));

  if (!active) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-52 shadow-xl rounded-2xl overflow-hidden bg-white border border-slate-200">
      {/* ── Camera preview ── */}
      <div className="relative w-full aspect-video bg-slate-900">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-cover"
          onLoadedData={() => setCameraOk(true)}
          onError={() => setCameraOk(false)}
        />
        {!cameraOk && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
            <CameraOff className="w-6 h-6 mb-1" />
            <span className="text-[10px]">No camera</span>
          </div>
        )}
        {/* Recording indicator */}
        <div className="absolute top-1.5 left-1.5 flex items-center gap-1 bg-black/50 rounded px-1.5 py-0.5">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
          <span className="text-white text-[9px] font-semibold tracking-wide">PROCTORED</span>
        </div>
      </div>

      {/* ── Integrity score bar ── */}
      <div className="px-3 pt-2 pb-1">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            <Shield className="w-3 h-3" /> Integrity
          </div>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${style.badge}`}>
            {score}/100 · {style.label}
          </span>
        </div>
        <div className="w-full h-1.5 bg-slate-100 rounded">
          <div className={`h-1.5 rounded transition-all ${style.bar}`} style={{ width: `${score}%` }} />
        </div>
      </div>

      {/* ── Violation count + expand toggle ── */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-1.5">
          <AlertTriangle className={`w-3.5 h-3.5 ${vCount > 0 ? "text-rose-500" : "text-slate-300"}`} />
          <span className={`text-xs font-semibold ${vCount > 0 ? "text-rose-600" : "text-slate-400"}`}>
            {vCount} violation{vCount !== 1 ? "s" : ""}
          </span>
        </div>
        {expanded
          ? <ChevronUp className="w-3 h-3 text-slate-400" />
          : <ChevronDown className="w-3 h-3 text-slate-400" />}
      </button>

      {/* ── Latest violation teaser (when collapsed) ── */}
      {!expanded && latest && (
        <div className="px-3 pb-2">
          <div className="flex items-start gap-1.5 text-[10px] text-slate-500 leading-tight">
            <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${SEV_DOT[latest.severity] || "bg-slate-400"}`} />
            <span className="line-clamp-2">{latest.description}</span>
          </div>
        </div>
      )}

      {/* ── Full violation list (expanded) ── */}
      {expanded && (
        <div className="max-h-36 overflow-y-auto px-3 pb-2 space-y-1">
          {violations.length === 0 && (
            <div className="text-[10px] text-slate-400 text-center py-2">No violations detected</div>
          )}
          {violations.slice().reverse().map((v, i) => (
            <div key={i} className="flex items-start gap-1.5 text-[10px] leading-tight">
              <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${SEV_DOT[v.severity] || "bg-slate-400"}`} />
              <div>
                <span className="font-semibold text-slate-700 capitalize">{(v.type || "").replace(/_/g, " ")}</span>
                <span className="text-slate-400 ml-1">{new Date(v.timestamp).toLocaleTimeString()}</span>
                <div className="text-slate-500">{v.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
